import {NfHotelComponentBase} from "../NfHotelComponentBase";
import {ComputedRef, reactive, Ref, watch} from "vue";
import type {translatedString} from "@/utilities/translations";
import type {BookingEngineConfigModel} from "@/serviceWorker/workerComponents/Models/BookingEngineConfigModel";
import type {NfConfigService} from "@/services/NfConfigService";
import type {
    HotelTranslatedConfigurationModel
} from "@/serviceWorker/workerComponents/Models/HotelTranslatedConfigurationModel";
import { NavigationPages } from "@/frontendClasses/NavigationPages";
import {isUndefined} from "lodash-es";
import type {InstanceConfiguration} from "@/frontendClasses";


const serviceInstanceTemplateVars: object[] = [];

/**
 * Klasa bazowa dla komponentów, które nie mogą występować samodzielnie (bez nadrzędnego komponentu)
 */
export abstract class NfHotelSubComponentBase extends NfHotelComponentBase {
    public isMainComponent = false;
    constructor() {
        super();
    }


    public subComponentReady = false;
    created() {
        super.created();
        watch(this.$props, this.onPropsChanged);
        watch(this.widgetId, this.onPropsChanged);
        watch(this.serviceInstanceId, this.onPropsChanged);

        if(this.isSubComponentReady()) {
            this.onSubComponentReady();
        }
    }
    protected isSubComponentReady() {
        return !!this.widgetId.value && !isUndefined(this.serviceInstanceId.value) && this.serviceInstanceId.value !== null;
    }
    onPropsChanged() {
        //console.debug(this.$options.name+' props changed',props?.widgetId,props?.serviceInstanceId);

        if(this.isSubComponentReady() && !this.subComponentReady) {
            this.onSubComponentReady();
        }
    }
    onSubComponentReady() {
        this.subComponentReady = true;
        this.$data.config = this._config.value;
        this.$data.translatedConfig = this._translatedConfig.value;
        if(!this._config.value) {
            this.configService.value.configurationPromise.then(c => this.$data.config = c);
        }
        if(!this._translatedConfig.value) {
            this.configService.value.translatedConfigurationPromise.then(t => this.$data.translatedConfig = t);
        }
    }
    declare _openModal:Function;
    declare _closeModal:Function;
    public static inject = {
        _openModal: {
            from: 'openModal'
        },
        _closeModal: {
            from: 'closeModal'
        },
        widgetId: {
            from: 'widgetId'
        },
        serviceInstanceId: {
            from: 'serviceInstanceId'
        },
        currentPage: {
            from: 'currentPage'
        },
        _config: {
            from: 'config'
        },
        _translatedConfig: {
            from: 'translatedConfig'
        },
        configService: {
            from: 'configService'
        },
        isInModal: {
            from: 'isInModal'
        },
        destroyBookingEngine: {
            from: 'destroyBookingEngine'
        },
        isDesktop: {
            from: 'isDesktop'
        },
        isOnPage: {
            from: 'isOnPage',
        },
        instanceConfiguration: {
            from: 'instanceConfiguration'
        }
    }
    declare _config: ComputedRef<BookingEngineConfigModel>;
    declare configService: ComputedRef<NfConfigService>;
    declare _translatedConfig: ComputedRef<HotelTranslatedConfigurationModel>;
    declare instanceConfiguration: ComputedRef<InstanceConfiguration>;

    inject = NfHotelSubComponentBase.inject;
    data() {
        return {
            ...super.data(),
            config: null as BookingEngineConfigModel|null,
            translatedConfig: null as HotelTranslatedConfigurationModel|null,
            /**
             * Globalny obiekt (per instancja booking engine)
             * w nim szablony mogą ustawiać zmienne, które będą dostępne we wszystkich innych szablonach w danej instancji booking engine
             */
            globalTemplateVars: this.getServiceInstanceTemplateVars()
        };
    }
    declare widgetId: Ref<number>;
    declare serviceInstanceId: Ref<number>;

    getServiceInstanceTemplateVars(): object {
        if (!serviceInstanceTemplateVars[this.serviceInstanceId.value]) {
            serviceInstanceTemplateVars[this.serviceInstanceId.value] = reactive({});
        }
        return serviceInstanceTemplateVars[this.serviceInstanceId.value];
    }

    openModals = {} as any;
    static methods: {
        openModal(this:NfHotelSubComponentBase, data: any): void;
        closeModal(this:NfHotelSubComponentBase, uniqueId: number|null): void;
        closeAllModals(this:NfHotelSubComponentBase): void;
        goToBeginning(this:NfHotelSubComponentBase): void;
        goToShop(this:NfHotelSubComponentBase): void;
        goToConfirmation(this:NfHotelSubComponentBase): void;
        formatPrice(this:NfHotelSubComponentBase, price: string|number, formattedDecimals:boolean): void;
    } = {
        openModal(this:NfHotelSubComponentBase, data: any) {
            const newModalUniqueId = this._openModal(data);
            this.openModals[newModalUniqueId] = true;
        },
        closeModal(this:NfHotelSubComponentBase, uniqueId: number|null) {
            if(!uniqueId ) {
                this._closeModal();
            } else {
                delete this.openModals[uniqueId];
                this._closeModal(uniqueId);
            }
        },
        closeAllModals(this:NfHotelSubComponentBase) {
            for(let innerModalId in this.openModals) {
                this._closeModal(innerModalId);
            }
            this.openModals = {};
        },

        goToBeginning(this:NfHotelSubComponentBase) {
            if(window.event) {
                window.event.preventDefault();
                window.event.stopImmediatePropagation();
                window.event.stopPropagation();
            }
            //@ts-ignore
            this.navigate(NavigationPages.beginningPage);
        },
        goToShop(this:NfHotelSubComponentBase) {
            if(window.event) {
                window.event.preventDefault();
                window.event.stopImmediatePropagation();
                window.event.stopPropagation();
            }
            //@ts-ignore
            this.navigate(NavigationPages.shopPage);

        },
        goToConfirmation(this:NfHotelSubComponentBase) {
            if(window.event) {
                window.event.preventDefault();
                window.event.stopImmediatePropagation();
                window.event.stopPropagation();
            }

            //@ts-ignore
            this.navigate(NavigationPages.confirmationPage);
        },
        formatPrice(this: NfHotelSubComponentBase, price: string | number, formattedDecimals: boolean = true) {
            return this.configService.value?.formatPrice(price, formattedDecimals);
        }
    }
    methods = NfHotelSubComponentBase.methods as Record<string,Function>;
    declare openModal: NfHotelSubComponentBase['methods']['openModal'];
    declare closeModal: NfHotelSubComponentBase['methods']['closeModal'];
    declare closeAllModals: NfHotelSubComponentBase['methods']['closeAllModals'];
    /**
     * Pokazuje okienko błędu
     * @param title tytuł okienka - tekst musi być podany przetłumaczony
     * @param text tekst w okienku - tekst musi być podany przetłumaczony
     */
    showError(title:translatedString,text:translatedString) {
        this.openModal({
            data: {
                title: title,
                text: text
            },
            templateName: 'errorWindow',
            position: 'screenCenter'
        });
    }
    beforeUnmount() {
        super.beforeUnmount();
        this.$options.methods.closeAllModals.apply(this);
    }

    static props = {
        /**
         * Możliwość nadpisania nazwy szablonu dla konkretnej instancji elementu
         */
        templateName: String,
        /**
         * Możliwość wrzucenia dodatkowych zmiennych dla szablonu
         */
        templateVarsInit: Object
    }

    props = NfHotelSubComponentBase.props;
}
